import { PASS_VIEW } from "enums";
import { ErrorMessage, Form, Formik, useFormikContext } from "formik";
import CloseIcon from "icons/close";
import ColorPicker from "layouts/authentication/register/component/color-picker";
import PassPreview from "layouts/authentication/register/component/pass-preview";
import React from "react";
import { handleConvertToPNG, invertTextColor } from "utils";
import * as yup from "yup";
import CDInput from "components/cd-input";
import CDButton from "components/cd-button";
import AdminV2ImageUpload from "./AdminV2ImageUpload";

function PassTypeButton({ value, label }) {
  const formik = useFormikContext();

  return (
    <label
      className={`tw-border tw-py-[15px] tw-text-regular tw-rounded-regular tw-flex-1 tw-flex tw-justify-center tw-cursor-pointer tw-bg-white ${
        formik.values.pass_type === value
          ? "tw-border-electricGreen tw-text-electricGreen"
          : "tw-border-gray-200 tw-text-gray-400"
      }`}
      htmlFor={`v2-${value}`}
    >
      {label}
      <input
        className="tw-hidden"
        id={`v2-${value}`}
        name="pass_type"
        type="radio"
        value={value}
        checked={formik.values.pass_type === value}
        onChange={() => formik.setFieldValue("pass_type", value)}
      />
    </label>
  );
}

function ListInputField({ label, name, type, placeholder }) {
  const formik = useFormikContext();

  const addField = () => {
    formik.setFieldValue(name, [...formik.values[name], ""]);
  };

  const removeField = (idx) => {
    formik.values[name].splice(idx, 1);
    formik.setValues({ ...formik.values });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-[10px]">
      <div className="tw-grow tw-flex tw-flex-col tw-gap-[7px]">
        <div className="tw-text-[#304861] tw-text-[15px] md:tw-text-[13px] tw-font-[500] tw-heading-[20px]">
          {label}
        </div>
        {formik.values[name].map((value, idx) => (
          <React.Fragment key={`${name}-${idx}`}>
            <div className="tw-bg-[#F2F5F5] tw-rounded-[5px] tw-min-h-[55px] md:tw-min-h-[40px] tw-px-[11px] tw-flex tw-items-center">
              <div className="tw-relative tw-w-full">
                <input
                  type={type}
                  placeholder={placeholder}
                  className="tw-w-full tw-h-full focus:tw-outline-none focus:tw-ring-transparent tw-bg-transparent tw-outline-none tw-border-none tw-p-0 tw-text-[16px] tw-placeholder-gray-300 md:tw-text-[14px] tw-heading-[20px] tw-text-black"
                  {...formik.getFieldProps(`${name}[${idx}]`)}
                />
                {idx !== 0 ? (
                  <button
                    type="button"
                    onClick={() => removeField(idx)}
                    className="tw-cursor-pointer tw-absolute tw-top-[5px] tw-right-[-35px]"
                  >
                    <CloseIcon />
                  </button>
                ) : null}
              </div>
            </div>
            <ErrorMessage
              name={`${name}[${idx}]`}
              component="div"
              className="error"
            />
          </React.Fragment>
        ))}
      </div>
      <button
        type="button"
        onClick={addField}
        className="tw-text-black tw-w-fit tw-font-inter tw-text-[15px] tw-font-semibold tw-leading-5 tw-cursor-pointer tw-ml-[5px]"
      >
        + add more
      </button>
    </div>
  );
}

const PassValidation = yup.object({
  firstName: yup.string().trim().required().label("First name"),
  lastName: yup.string().trim().required().label("Last name"),
  backgroundColor: yup.string().trim().required().label("Your Colour"),
  organisationName: yup.string().trim().required().label("Organisation name"),
  jobTitle: yup.string().trim().required().label("Job title"),
  email: yup
    .array()
    .of(yup.string().email().required().label("Email"))
    .min(1)
    .required()
    .label("Email"),
  phone: yup.array().of(yup.string().label("Phone")).label("Phone"),
  companyURL: yup.string().trim().label("Company URL"),
  linkedInURL: yup.string().trim().label("LinkedIn URL"),
  stripImage: yup.string().trim(),
  logoImage: yup.string().trim(),
});

function CreatePassV2({
  initState,
  onSubmit,
  processing,
  error,
  btnLabel,
  showDeviceType = true,
  logoImageInput = false,
  stripImageInput = false,
  initialValid,
  additionalFields,
}) {
  const handleSubmit = async (form) => {
    const formData = { ...form };

    if (!logoImageInput || (!form.logoImageName && !form.logoImage)) {
      formData.logoImage = await handleConvertToPNG("default-logo");
    }

    if (!stripImageInput || (!form.stripImageName && !form.stripImage)) {
      formData.stripImage = await handleConvertToPNG("default-strip");
    }

    onSubmit({
      ...formData,
      labelColor: invertTextColor(form.backgroundColor),
    });
  };

  return (
    <Formik
      initialValues={{ ...initState }}
      validationSchema={PassValidation}
      onSubmit={handleSubmit}
      isInitialValid={initialValid || false}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="tw-w-full">
            <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-[32px] lg:tw-gap-[48px] tw-items-start tw-justify-center">
              <div className="md:tw-sticky md:tw-top-6">
                <PassPreview view={formik.values.pass_type} values={formik.values} />
              </div>

              <div className="tw-w-full tw-max-w-[420px] tw-flex tw-flex-col tw-gap-[32px]">
                <div className="tw-flex tw-flex-col tw-gap-[22px]">
                  {showDeviceType ? (
                    <div>
                      <div className="tw-text-navyBlue tw-leading-regular tw-flex tw-cursor-default tw-items-center tw-gap-[5px] tw-text-[15px] md:tw-text-[13px] ">
                        <div className="tw-flex tw-items-center tw-gap-[5px]">
                          <div>Your device type*</div>
                        </div>
                      </div>
                      <div className="tw-flex tw-pt-[8px] tw-gap-4 tw-justify-between">
                        <PassTypeButton label={"Apple"} value={PASS_VIEW.APPLE} />
                        <PassTypeButton label={"Google"} value={PASS_VIEW.ANDROID} />
                      </div>
                    </div>
                  ) : null}

                  <ColorPicker label={"Your Colour*"} name={"backgroundColor"} />

                  {logoImageInput ? (
                    <AdminV2ImageUpload
                      name="logoImage"
                      label="Top left icon* (1200x1200px)"
                    />
                  ) : null}

                  {stripImageInput ? (
                    <AdminV2ImageUpload
                      name="stripImage"
                      label="Strip image* (1033x407px)"
                    />
                  ) : null}

                  <div className="tw-border-b-[1px] tw-border-[#E7E7E7] tw-border-dashed"></div>

                  <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[25px] md:tw-gap-[8px]">
                    <CDInput label={"First name"} name={"firstName"} required />
                    <CDInput label={"Last name"} name={"lastName"} required />
                  </div>

                  <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[25px] md:tw-gap-[8px]">
                    <CDInput
                      label={"Organisation"}
                      name={"organisationName"}
                      placeholder="Aramco"
                      required
                    />
                    <CDInput
                      label={"Job title"}
                      name={"jobTitle"}
                      required
                      placeholder="Vice President"
                    />
                  </div>

                  <ListInputField
                    label={"Email*"}
                    name={"email"}
                    type="email"
                    placeholder=""
                  />

                  <ListInputField
                    label={"Phone"}
                    name={"phone"}
                    type="tel"
                    placeholder="+966 123456789"
                  />

                  <CDInput
                    label={"Company URL"}
                    name={"companyURL"}
                    placeholder=""
                  />

                  <CDInput
                    label={"LinkedIn URL"}
                    name={"linkedInURL"}
                    placeholder="www.linked.com/in/"
                  />

                  {additionalFields}

                  <CDButton
                    disabled={!formik.isValid}
                    processing={processing}
                    label={btnLabel || "Create"}
                  />
                  <div className="error">{error}</div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default CreatePassV2;
